import { Box, Card, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import DefaultButton from "../simple/DefaultButton";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BasicCourseDetailPanel from "../simple/BasicCourseDetailPanel";
import TeamIcon from "../../assets/icons/team.svg";
import BookIcon from "../../assets/icons/book.svg";
import BookClosedIcon from "../../assets/icons/book-closed.svg";
import BarChartIcon from "../../assets/icons/bar-chart.svg";
import { useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";

import ProgressBar from "../simple/ProgressBar";

export default function ProgramInfoCard({
  trainingProgram,
  totalSteps,
  isMobile,
}) {
  const theme = useTheme();
  const [scoringStats, setScoringStats] = React.useState({
    withScoring: 0,
    withoutScoring: 0,
  });
  const navigate = useNavigate();

  let nextScenario = null;

  if (trainingProgram?.steps?.length) {
    for (const step of trainingProgram.steps) {
      const chapterSteps = step.chapter?.steps || [];
      for (const scenarioStep of chapterSteps) {
        const scenario = scenarioStep.scenario;
        if (!scenario.completed && !scenario.locked) {
          nextScenario = scenario;
          break;
        }
      }
      if (nextScenario) break;
    }
  }

  useEffect(() => {
    if (!trainingProgram?.steps) return;

    let withScoring = 0;
    let withoutScoring = 0;

    trainingProgram.steps.forEach((chapterStep) => {
      chapterStep.chapter.steps.forEach((step) => {
        if (step.scenario?.has_scoring_engine) {
          withScoring++;
        } else {
          withoutScoring++;
        }
      });
    });

    setScoringStats({ withScoring, withoutScoring });
  }, [trainingProgram]);

  if (!trainingProgram) {
    return (
      <Card
        sx={{
          mb: theme.customSpacing["4xl"],
          padding: theme.containers.paddingDesktop,
          color: theme.palette.text.secondary,
          border: `2px solid ${theme.palette.border.tertiary}`,
          borderRadius: theme.shape.radius,
          textAlign: "center",
          backgroundColor: "transparent",
        }}
      >
        <Typography sx={{ fontWeight: theme.typography.fontWeightMedium }}>
          Your program is still loading...
        </Typography>
        <Typography sx={{ color: theme.palette.text.gray }}>
          If this takes too long, please refresh the page.
        </Typography>
      </Card>
    );
  }

  return (
    <Box
      id="training-program-overview"
      sx={{
        display: "flex",
        flexDirection: "row",
        color: theme.palette.text.secondary,
        maxHeight: "382px",
        marginBottom: "20px",
        [theme.breakpoints.down("md")]: {
          flexWrap: "wrap",
          maxHeight: "none",
        },
        "@media (max-width:1080px)": {
          flexWrap: "wrap",
          maxHeight: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "40%",
          backgroundColor: "white",
          backgroundImage: `url(${trainingProgram?.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: theme.shape.radius.md,
          justifyContent: "flex-end",
          mr: theme.customSpacing["xl"],
          [theme.breakpoints.down("md")]: {
            width: "100%",
            marginRight: 0,
            marginBottom: "20px",
            minHeight: "200px",
          },
          "@media (max-width:1080px)": {
            width: "100%",
            marginRight: 0,
            marginBottom: "20px",
            minHeight: "250px",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            zIndex: 1,
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(0,0,0,0.65) 100%)",
            borderRadius: theme.shape.radius.md,
            padding: isMobile ? "1.25rem" : theme.containers.paddingDesktop,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Poppins",
              fontSize: "clamp(0.75rem, 0.75rem + 2vw, 1.3rem)",
              fontStyle: "normal",
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: "1.2",
              mb: "10px",
            }}
          >
            {trainingProgram?.name}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontFamily: "Poppins",
              fontSize: theme.typography.textXS.fontSize,
              fontStyle: "normal",
              fontWeight: theme.typography.fontWeightRegular,
              lineHeight: "1.2",
            }}
          >
            {trainingProgram.author
              ? `A Program By ${trainingProgram.author} | `
              : null}
            {trainingProgram.release_date
              ? `Released ${trainingProgram.release_date}`
              : null}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: theme.palette.background.primary,
          justifyContent: "space-evenly",
          color: theme.palette.text.primary,
        }}
      >
        <Typography
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textXS.fontSize,
            textTransform: "uppercase",
            display: isMobile ? "none" : "block",
            mb: `${theme.customSpacing.lg}`,
          }}
        >
          Program Overview
        </Typography>
        {trainingProgram?.description && (
          <Typography
            variant="body2"
            sx={{
              mb: `${theme.customSpacing["2xl"]}`,
              fontSize: theme.typography.textXS.fontSize,
              lineHeight: theme.typography.textXS.lineHeight,
              color: theme.palette.text.gray,
              whiteSpace: "pre-wrap",
            }}
          >
            {trainingProgram?.description}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            padding: 0,
            alignItems: "flex-start",
            gap: theme.customSpacing["lg"],
            alignSelf: "stretch",
            flexWrap: "wrap",
            mb: "20px",
          }}
        >
          <BasicCourseDetailPanel
            title={"Difficulty Level"}
            details={
              trainingProgram.difficulty_level
                ? trainingProgram.difficulty_level.charAt(0).toUpperCase() +
                  trainingProgram.difficulty_level.slice(1)
                : "Beginner"
            }
            icon={<img src={BarChartIcon} alt="Bar Charts" />}
          />
          <BasicCourseDetailPanel
            title={"Topics"}
            details={totalSteps}
            icon={<img src={BookClosedIcon} alt="" />}
          />
          <BasicCourseDetailPanel
            title={
              isMobile ? "Skill Sessions" : "Total Skills Development Sessions"
            }
            details={scoringStats.withoutScoring.toString()}
            icon={<img src={BookIcon} alt="Books" />}
          />
          <BasicCourseDetailPanel
            title={isMobile ? "Simulations" : "Total Simuations"}
            details={scoringStats.withScoring.toString()}
            icon={<img src={TeamIcon} alt="Team" />}
          />
        </Box>
        {nextScenario === null ? (
          <Alert variant="filled" severity="success">
            Course completed
          </Alert>
        ) : (
          <DefaultButton
            wideOnMobile
            size="sm"
            onClick={() => navigate(`/dashboard/simulate/${nextScenario.id}`)}
            endIcon={<ArrowForwardRoundedIcon />}
          >
            Up Next: {nextScenario?.name}
          </DefaultButton>
        )}
        <Box sx={{ height: theme.customSpacing["2xl"] }} />
        <ProgressBar trainingProgram={trainingProgram} />
      </Box>
    </Box>
  );
}
